import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { site } from "@/mocks/site";

const FORM_ENDPOINT = "https://readdy.ai/api/form/daij75roh653ivfvoe6g";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactPage() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  useSeo({
    title: "联系我们 | 深瞳档案 线索提交与内容更正",
    description:
      "联系深瞳档案：提交可核对的信息线索、反馈内容更正或沟通栏目选题。请通过表单或邮箱与我们取得联系，我们会在工作时间内回复。",
    keywords: "联系我们,线索提交,内容更正,读者反馈,深瞳档案",
    path: "/contact",
    type: "website",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    formData.delete("contact_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请调整内容后重试。"
            : typeof serverMsg === "string" && serverMsg
              ? serverMsg
              : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们",
      url: "https://www.shentongdangan.cn/contact",
      inLanguage: "zh-CN",
      description: "联系深瞳档案，提交线索或反馈内容更正。",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.shentongdangan.cn/" },
        { "@type": "ListItem", position: 2, name: "联系我们", item: "https://www.shentongdangan.cn/contact" },
      ],
    },
  ];

  return (
    <>
      <JsonLd data={schema} />
      <div className="border-b border-background-200 bg-background-100">
        <div className="container-page">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "联系我们" }]} />
          <div className="pb-12">
            <span className="text-xs font-medium tracking-[0.2em] text-primary-600">CONTACT</span>
            <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-5xl">联系我们</h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              无论是提供线索、指出错误，还是想讨论一个选题，都欢迎留言。请尽量描述清楚具体的事件与来源，
              方便我们核对。
            </p>
          </div>
        </div>
      </div>

      <div className="container-page grid grid-cols-1 gap-10 py-12 lg:grid-cols-[1fr_320px]">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950">给我们留言</h2>
          <p className="mt-2 text-sm text-foreground-500">
            带 <span className="text-primary-600">*</span> 的为必填项，正文最多 500 字。
          </p>

          {status === "success" && (
            <div className="mt-5 flex items-start gap-3 rounded-md border border-accent-300 bg-accent-50 px-4 py-3">
              <i className="ri-checkbox-circle-line mt-0.5 text-lg text-accent-700" aria-hidden="true" />
              <p className="text-sm text-accent-900">
                留言已提交成功，感谢你的信息。我们会在 {site.workingHours} 之间查看并回复。
              </p>
            </div>
          )}

          {status === "error" && (
            <div className="mt-5 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 px-4 py-3">
              <i className="ri-error-warning-line mt-0.5 text-lg text-primary-700" aria-hidden="true" />
              <p className="text-sm text-primary-900">{formError}</p>
            </div>
          )}

          <form
            id="contact-form"
            data-readdy-form
            action={FORM_ENDPOINT}
            method="post"
            onSubmit={handleSubmit}
            className="mt-6 flex flex-col gap-5"
          >
            <input
              type="text"
              name="contact_alt"
              className="alt-field-guard"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-2 block text-sm font-medium text-foreground-800">
                  称呼 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  placeholder="怎么称呼你"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground-800">
                  邮箱 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="用于接收回复"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div>
              <label htmlFor="topic" className="mb-2 block text-sm font-medium text-foreground-800">
                留言类型
              </label>
              <select
                id="topic"
                name="topic"
                defaultValue="提供线索"
                className="h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="提供线索">提供线索</option>
                <option value="内容更正">内容更正</option>
                <option value="选题建议">选题建议</option>
                <option value="合作沟通">合作沟通</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="mb-2 block text-sm font-medium text-foreground-800">
                留言内容 <span className="text-primary-600">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={6}
                onChange={(e) => setMessageLength(e.target.value.length)}
                placeholder="请尽量描述清楚事件、时间与可核对的信息来源。"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm leading-relaxed text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              <p className="mt-1.5 text-right text-xs text-foreground-400">{messageLength} / 500</p>
            </div>

            <button
              type="submit"
              disabled={status === "submitting"}
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "submitting" ? (
                <>
                  <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                  正在提交…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line" aria-hidden="true" />
                  提交留言
                </>
              )}
            </button>
          </form>
        </div>

        <aside className="flex flex-col gap-5">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">直接联系</h2>
            <ul className="mt-4 space-y-3 text-sm text-foreground-600">
              <li className="flex items-start gap-2.5">
                <i className="ri-mail-line mt-0.5 text-primary-600" aria-hidden="true" />
                <span>
                  <a href={`mailto:${site.email}`} className="transition-colors hover:text-primary-700">
                    {site.email}
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="ri-time-line mt-0.5 text-primary-600" aria-hidden="true" />
                <span>{site.workingHours}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="ri-shield-keyhole-line mt-0.5 text-primary-600" aria-hidden="true" />
                <span>未经你同意，我们不会公开任何个人信息。</span>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">先看看这些</h2>
            <ul className="mt-3 space-y-2.5">
              <li>
                <Link
                  to="/faq"
                  className="flex items-center gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                >
                  <i className="ri-arrow-right-s-line text-primary-500" aria-hidden="true" />
                  常见问题
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="flex items-center gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                >
                  <i className="ri-arrow-right-s-line text-primary-500" aria-hidden="true" />
                  内容原则
                </Link>
              </li>
              <li>
                <Link
                  to="/channels"
                  className="flex items-center gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                >
                  <i className="ri-arrow-right-s-line text-primary-500" aria-hidden="true" />
                  栏目总览
                </Link>
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </>
  );
}